/**
 * TabSlider
 *
 * Navigation component for tabs in the split view area.
 * Similar to WorkspaceSlider in sidebar - provides workspace-like navigation between tabs.
 * Shows dots for each tab, allows creating new tabs, and supports keyboard shortcuts.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlus, 
  FaCheck, 
  FaTimes, 
  FaExternalLinkAlt,
  FaTrash,
} from "react-icons/fa";
import {
  useTabs,
  useCurrentTabId,
  useCurrentTabIndex,
  useTabsActions,
  type Tab,
} from "@/hooks/use-tabs-store";
import { MotionDiv, MotionButton } from "../arc-sidebar/motion-types";
import { useSplitView } from "./SplitViewProvider";

interface TabSliderProps {
  /** Whether the slider is always visible (even with 1 tab) */
  alwaysShow?: boolean;
  /** Accent color for the active tab */
  accentColor?: string;
}

const TabSlider: React.FC<TabSliderProps> = ({
  alwaysShow = false,
  accentColor = "var(--arc-accent)",
}) => {
  const tabs = useTabs();
  const currentTabId = useCurrentTabId();
  const currentIndex = useCurrentTabIndex();
  const { 
    createTab, 
    selectTab, 
    renameTab, 
    deleteTab,
    goToPreviousTab,
    goToNextTab,
    goToTabByIndex,
    getTabRoute,
  } = useTabsActions();
  const { openInNewTab } = useSplitView();

  const [isCreatingTab, setIsCreatingTab] = useState(false);
  const [newTabName, setNewTabName] = useState("");
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [swipeDirection, setSwipeDirection] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const prevIndexRef = useRef<number>(currentIndex);

  // Track direction for animation
  useEffect(() => {
    if (currentIndex > prevIndexRef.current) {
      setSwipeDirection(1);
    } else if (currentIndex < prevIndexRef.current) {
      setSwipeDirection(-1);
    }
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Handle create tab
  const handleCreateTab = useCallback(() => {
    const name = newTabName.trim() || undefined;
    createTab(name);
    setNewTabName("");
    setIsCreatingTab(false);
  }, [newTabName, createTab]);

  // Handle key press in create input
  const handleCreateKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleCreateTab();
      } else if (e.key === "Escape") {
        setIsCreatingTab(false);
        setNewTabName("");
      }
    },
    [handleCreateTab]
  );

  // Handle rename
  const handleStartRename = useCallback((tab: Tab) => {
    setEditingTabId(tab.id);
    setEditingName(tab.name);
  }, []);

  const handleRename = useCallback(() => {
    if (editingTabId && editingName.trim()) {
      renameTab(editingTabId, editingName.trim());
    }
    setEditingTabId(null);
    setEditingName("");
  }, [editingTabId, editingName, renameTab]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleRename();
      } else if (e.key === "Escape") {
        setEditingTabId(null);
        setEditingName("");
      }
    },
    [handleRename]
  );

  // Handle delete tab
  const handleDeleteTab = useCallback((tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tabs.length > 1) {
      deleteTab(tabId);
    }
  }, [tabs.length, deleteTab]);

  // Handle open tab in new browser tab
  const handleOpenInNewBrowserTab = useCallback((tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const route = getTabRoute(tabId);
    if (route) {
      window.open(route, "_blank");
    }
  }, [getTabRoute]);

  // Focus input when creating or editing
  useEffect(() => {
    if (isCreatingTab && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingTab]);

  useEffect(() => {
    if (editingTabId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTabId]);

  // Keyboard shortcuts for tab navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in input
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      // Alt + Left/Right to navigate tabs
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          goToPreviousTab();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          goToNextTab();
        }
      }

      // Alt + number to select tab (1-9)
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.key >= "1" && e.key <= "9") {
        const index = parseInt(e.key) - 1;
        if (index < tabs.length) {
          e.preventDefault();
          goToTabByIndex(index);
        }
      }

      // Alt + T to create new tab
      if (e.altKey && e.key.toLowerCase() === "t" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setIsCreatingTab(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPreviousTab, goToNextTab, goToTabByIndex, tabs.length]);

  // Don't show if only one tab and not alwaysShow
  if (tabs.length <= 1 && !alwaysShow && !isCreatingTab) {
    return null;
  }

  const currentTab = tabs[currentIndex];

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50">
      <MotionDiv
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.9 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        className="flex items-center gap-2 py-2 px-4 bg-surface-elevated/95 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl select-none"
        style={{
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05) inset",
        }}
      >
        {/* Previous button */}
        <MotionButton
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={goToPreviousTab}
          disabled={currentIndex <= 0}
          className={`p-1.5 rounded-full transition-all ${
            currentIndex <= 0
              ? "text-content-disabled cursor-not-allowed"
              : "text-content-tertiary hover:text-content-primary hover:bg-white/10"
          }`}
          title="Tab précédent (Alt+←)"
        >
          <FaChevronLeft size={10} />
        </MotionButton>

        {/* Tab dots */}
        <div className="flex items-center gap-1.5 px-1">
          {tabs.map((tab, index) => (
            <MotionButton
              key={tab.id}
              onClick={() => selectTab(tab.id)}
              onDoubleClick={() => handleStartRename(tab)}
              animate={{
                scale: tab.id === currentTabId ? 1.3 : 1,
                backgroundColor:
                  tab.id === currentTabId
                    ? tab.color || accentColor
                    : "rgba(128, 128, 128, 0.4)",
              }}
              whileHover={{ scale: 1.4 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="w-2 h-2 rounded-full"
              style={{
                boxShadow:
                  tab.id === currentTabId
                    ? `0 0 12px ${tab.color || accentColor}`
                    : undefined,
              }}
              title={`${tab.name} (Alt+${index + 1})`}
            />
          ))}

          {/* Create new tab button */}
          <MotionButton
            onClick={() => setIsCreatingTab(true)}
            animate={{
              scale: isCreatingTab ? 1.3 : 1,
              backgroundColor: isCreatingTab
                ? accentColor
                : "rgba(128, 128, 128, 0.4)",
            }}
            whileHover={{ scale: 1.4 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="w-2 h-2 rounded-full flex items-center justify-center"
            title="Nouveau tab (Alt+T)"
          >
            <FaPlus size={5} className="text-white/60" />
          </MotionButton>
        </div>

        {/* Next button */}
        <MotionButton
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={goToNextTab}
          disabled={currentIndex >= tabs.length - 1}
          className={`p-1.5 rounded-full transition-all ${
            currentIndex >= tabs.length - 1
              ? "text-content-disabled cursor-not-allowed"
              : "text-content-tertiary hover:text-content-primary hover:bg-white/10"
          }`}
          title="Tab suivant (Alt+→)"
        >
          <FaChevronRight size={10} />
        </MotionButton>

        {/* Separator */}
        <div className="w-px h-4 bg-white/10" />

        {/* Current tab name (editable) */}
        <AnimatePresence mode="wait">
          {editingTabId === currentTabId ? (
            <MotionDiv
              key="editing"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex items-center gap-1"
            >
              <input
                ref={editInputRef}
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                onBlur={handleRename}
                className="px-2 py-0.5 text-xs rounded-full border border-white/20 bg-white/5 focus:outline-none focus:ring-1 focus:ring-accent w-24"
              />
            </MotionDiv>
          ) : (
            <MotionDiv
              key={currentTab?.id || "create"}
              initial={{ opacity: 0, x: swipeDirection * 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -swipeDirection * 10 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
                mass: 0.8,
              }}
              className="flex items-center gap-1.5"
            >
              <span
                className="text-xs font-medium text-content-secondary cursor-pointer hover:text-content-primary transition-colors max-w-[120px] truncate"
                onDoubleClick={() => currentTab && handleStartRename(currentTab)}
                title="Double-cliquez pour renommer"
              >
                {currentTab?.name || "Nouveau Tab"}
              </span>

              {/* Actions for current tab */}
              {currentTab && (
                <div className="flex items-center gap-0.5">
                  {/* Open in new browser tab */}
                  <MotionButton
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => handleOpenInNewBrowserTab(currentTab.id, e)}
                    className="p-1 rounded-full text-content-tertiary hover:text-content-primary hover:bg-white/10"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <FaExternalLinkAlt size={9} />
                  </MotionButton>

                  {/* Delete tab (only if more than 1 tab) */}
                  {tabs.length > 1 && (
                    <MotionButton
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleDeleteTab(currentTab.id, e)}
                      className="p-1 rounded-full text-content-tertiary hover:text-red-400 hover:bg-red-500/10"
                      title="Fermer ce tab"
                    >
                      <FaTrash size={9} />
                    </MotionButton>
                  )}
                </div>
              )}
            </MotionDiv>
          )}
        </AnimatePresence>

        {/* Create tab input (shown inline) */}
        <AnimatePresence>
          {isCreatingTab && (
            <MotionDiv
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              className="flex items-center gap-1 overflow-hidden"
            >
              <div className="w-px h-4 bg-white/10 mr-1" />
              <input
                ref={inputRef}
                type="text"
                value={newTabName}
                onChange={(e) => setNewTabName(e.target.value)}
                onKeyDown={handleCreateKeyDown}
                placeholder="Nom du tab"
                className="px-2 py-0.5 text-xs rounded-full border border-white/20 bg-white/5 focus:outline-none focus:ring-1 focus:ring-accent w-24"
              />
              <MotionButton
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleCreateTab}
                className="p-1 rounded-full text-emerald-400 hover:bg-emerald-500/10"
              >
                <FaCheck size={9} />
              </MotionButton>
              <MotionButton
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setIsCreatingTab(false);
                  setNewTabName("");
                }}
                className="p-1 rounded-full text-red-400 hover:bg-red-500/10"
              >
                <FaTimes size={9} />
              </MotionButton>
            </MotionDiv>
          )}
        </AnimatePresence>
      </MotionDiv>
    </div>
  );
};

TabSlider.displayName = "TabSlider";

export default TabSlider;
